import isEqual from 'lodash/isEqual';
import { Plugin, Context } from '@nuxt/types';

import Kilkaya from '@analytics/trackers/Kilkaya/Kilkaya';
import { KilkayaAttributes } from '@analytics/trackers/Kilkaya/types/Kilkaya';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { PianoState } from '@piano/types/pianoState';
import { State as ArticleState } from '@root/modules/article/types/article';

const kilkaya: Plugin = async (ctx) => {
  const { app, store } = ctx;
  const { locale, application } = app.$channelConfig('settings');

  const kilkaya = new Kilkaya();
  await kilkaya.init();

  /* Attributes getters */
  function getPaywallAttribute(activeArticle: ArticleData) {
    if (!activeArticle.content.paywall.enabled) {
      return 'open';
    }

    // For paywall enabled articles, check metered access
    if (store.state.piano.access.metered) {
      return 'metered';
    }

    // For paywall enabled articles, check channel access
    if (store.state.piano.access.channelAccess) {
      return 'open';
    }

    return 'hard';
  }

  function getPageTypeAttribute(routeName: Context['route']['name']) {
    const pageNameToTypeMap: Record<string, string> = {
      authors: 'Author',
      author: 'Author',
      latest: 'Latest',
      popular: 'Popular',
      search: 'Search',
      category: 'Category',
      topic: 'Tag',
      topics: 'Tags',
      frontpage: 'Frontpage',
      channel: 'Channel',
      customPage: 'CustomPage',
      bookmarks: 'Bookmarks',
      article: 'Article',
      articleComments: 'Comments',
    };

    if (typeof routeName !== 'string') {
      return 'Unknown';
    }

    return pageNameToTypeMap[routeName] || routeName;
  }

  function getKilkayaDefaultPageAttributes(route: Context['route']) {
    const applicationType = application.type;

    return {
      type: getPageTypeAttribute(route.name),
      login: Number(store.state.piano.isLoggedIn),
      subscriptionResource: store.state.piano.subscriptionInfo?.name,
      subscriber: Number(store.state.piano.access.channelAccess),
      url: location.href,
      referer: document.referrer,
      locale,
      ...(applicationType && { channel: applicationType }),
    };
  }

  function getKilkayaArticleAttributes(activeArticle: ArticleData): KilkayaAttributes {
    return {
      paywall: getPaywallAttribute(activeArticle),
      comments: activeArticle.statistics.comments,
    };
  }

  function getKilkayaArticlePageAttributes(activeArticle: ArticleData, route: Context['route']): KilkayaAttributes {
    return {
      ...getKilkayaDefaultPageAttributes(route),
      ...getKilkayaArticleAttributes(activeArticle),
      url: store.state.article.activeArticlePath ? new URL(store.state.article.activeArticlePath, location.origin).href : location.href,
    };
  }

  /* Page view dispatcher */
  function trackPageByType(route: Context['route'], activeArticle?: ArticleData) {
    if (!app.router) {
      return;
    }

    let attributes;

    switch (route.name) {
      case 'article':
        if (activeArticle) {
          attributes = getKilkayaArticlePageAttributes(activeArticle, route);
        }
        break;
      case 'articleComments':
        // Intentionally untracked for now; pagetype mapping is already defined in getPageTypeAttribute
        break;
      default:
        attributes = getKilkayaDefaultPageAttributes(route);
        break;
    }

    if (attributes) {
      kilkaya.pageView(attributes);
    }
  }

  /* Event listeners and watchers */
  // Track page view when in-app navigation happens
  app.router!.afterEach((to, from) => {
    if (!from.name) {
      return;
    }

    const navigationFromArticle = from.name === 'article' || from.name === 'articleComments';
    const navigationToArticle = to.name === 'article' || to.name === 'articleComments';
    // Is handled by activeArticlePath watcher
    const articleToArticleNavigation = navigationFromArticle && navigationToArticle;

    if (!articleToArticleNavigation && store.state.piano.isScriptInited) {
      trackPageByType(to, store.state.article.activeArticle);
    }
  });

  // Send another pageview if the page is restored from bfcache.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && store.state.piano.isScriptInited) {
      trackPageByType(app.router!.currentRoute, store.state.article.activeArticle);
    }
  });

  // Track article page view when user opens article or scrolls to the next article
  store.watch(
    (state: { article: ArticleState }) => state.article.activeArticlePath,
    async (activeArticlePath: ArticleState['activeArticlePath']) => {
      if (activeArticlePath && app.router && store.state.article.activeArticle && store.state.piano.isScriptInited) {
        const routeName = app.router.currentRoute.name;
        if (routeName === 'article' || routeName === 'issue-article') {
          trackPageByType(app.router.currentRoute, store.state.article.activeArticle);
        }
      }
    }
  );

  // Track page view after Piano is initialized
  store.watch(
    (state: { piano: PianoState }) => state.piano.isScriptInited,
    (isScriptInited: PianoState['isScriptInited']) => {
      if (!isScriptInited) {
        return;
      }

      watchPianoAccess();
      watchPianoLoginState();
    },
    { immediate: true }
  );

  // Track page view when Piano login state changes
  function watchPianoLoginState() {
    store.watch(
      (state: { piano: PianoState }) => state.piano.isLoggedIn,
      async () => {
        trackPageByType(app.router!.currentRoute, store.state.article.activeArticle);
      }
    );
  }

  // Track page view when Piano access changes
  function watchPianoAccess() {
    store.watch(
      (state: { piano: PianoState }) => state.piano.access,
      async (newAccess: PianoState['access'], oldAccess: PianoState['access']) => {
        if (isEqual(newAccess, oldAccess)) {
          return;
        }

        trackPageByType(app.router!.currentRoute, store.state.article.activeArticle);
      },
      { immediate: true }
    );
  }
};

export default kilkaya;
